import React from 'react'
import styled from 'styled-components'
import { MapControl } from '../../atoms/MapControl'
import { Icon } from '../../atoms/Icon'
import { captureMapWithMarkers, downloadMapImage } from './Map.print'
import mapboxgl from 'mapbox-gl'

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;

  > *:first-child:not(:only-child) {
    border-bottom: none;
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  > *:last-child:not(:only-child) {
    border-top: none;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }

  > *:not(:first-child):not(:last-child) {
    border-top: none;
    border-bottom: none;
    border-radius: 0 !important;
  }
`

const LayerImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  display: block;
  padding: 5px;
  border-radius: 10px;
`

interface MapControlsProps {
  showControls: boolean
  layersPanelOpen: boolean
  setLayersPanelOpen: (open: boolean) => void
  legendPanelOpen: boolean
  setLegendPanelOpen: (open: boolean) => void
  showDimensionControl: boolean
  dimension: '2D' | '3D'
  onToggle3D: () => void
  showBuiltInPrintControl: boolean
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapContainer: React.MutableRefObject<HTMLDivElement | null>
  CustomPrintControl?: React.ComponentType<any>
  onChangeMapStyle?: () => void
  mapStylePanelOpen?: boolean
  activeLayerImage?: string
  onFlyToUserLocation: () => void
}

export const MapControlsGroup: React.FC<MapControlsProps> = ({
  showControls,
  layersPanelOpen,
  setLayersPanelOpen,
  legendPanelOpen,
  setLegendPanelOpen,
  showDimensionControl,
  dimension,
  onToggle3D,
  showBuiltInPrintControl,
  map,
  mapContainer,
  CustomPrintControl,
  onChangeMapStyle,
  mapStylePanelOpen,
  activeLayerImage,
  onFlyToUserLocation,
}) => {
  const handlePrintMap = async () => {
    if (!map.current || !mapContainer.current) return

    try {
      const imageUrl = await captureMapWithMarkers({
        map: map.current,
        mapContainer: mapContainer.current,
      })
      if (imageUrl) {
        await downloadMapImage(imageUrl)
      } else {
        console.error('Failed to generate map image')
        // eslint-disable-next-line no-alert
        alert('Failed to export map. Please try again.')
      }
    } catch (error) {
      console.error('Failed to capture map:', error)
      // eslint-disable-next-line no-alert
      alert('Failed to export map. Please try again.')
    }
  }

  if (!showControls) return null

  return (
    <ControlsWrapper>
      <ControlGroup>
        <MapControl
          icon={
            activeLayerImage ? (
              <LayerImage src={activeLayerImage} alt="Active layer" />
            ) : (
              <svg viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            )
          }
          title="Layers"
          active={layersPanelOpen}
          onClick={() => setLayersPanelOpen(!layersPanelOpen)}
          style={{ position: 'relative', overflow: 'hidden' }}
        />

        <ButtonGroup>
          {showDimensionControl && (
            <MapControl
              icon={
                <svg viewBox="0 0 24 24">
                  <path d="M12 2L2 9v11l10 7 10-7V9L12 2z" />
                  <path d="M12 22V12" />
                  <path d="M12 12L2 9" />
                  <path d="M12 12l10-3" />
                </svg>
              }
              title="3D View"
              active={dimension === '3D'}
              onClick={onToggle3D}
            />
          )}

          <MapControl
            icon={
              <svg viewBox="0 0 24 24">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="M2 12h20"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="M12 2a15 15 0 010 20a15 15 0 010-20"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="M4.93 4.93a10 10 0 0114.14 0"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="M4.93 19.07a10 10 0 0114.14 0"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
              </svg>
            }
            title="Mapstyle"
            active={mapStylePanelOpen || false}
            onClick={onChangeMapStyle}
          />
        </ButtonGroup>
      </ControlGroup>

      <ControlGroup>
        <MapControl
          icon={
            <svg viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <rect x="7" y="7" width="3" height="3" />
              <rect x="7" y="14" width="3" height="3" />
              <line x1="14" y1="8.5" x2="17" y2="8.5" />
              <line x1="14" y1="15.5" x2="17" y2="15.5" />
            </svg>
          }
          title="Legend"
          active={legendPanelOpen}
          onClick={() => setLegendPanelOpen(!legendPanelOpen)}
        />

        {/* Print Control */}
        {CustomPrintControl ? (
          <CustomPrintControl />
        ) : (
          showBuiltInPrintControl && (
            <MapControl
              icon={
                <svg viewBox="0 0 24 24">
                  <path d="M6 9V2h12v7" />
                  <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                  <path d="M6 14h12v8H6z" />
                </svg>
              }
              title="Print Map"
              onClick={handlePrintMap}
            />
          )
        )}
      </ControlGroup>
    </ControlsWrapper>
  )
}
